import React, { useEffect, useRef, useState } from 'react';
import { SpeedReadingLesson, formatLessonDuration } from './trainingApi';

interface LessonGridProps {
  lessons: SpeedReadingLesson[];
  canManage: boolean;
  onSelect: (lesson: SpeedReadingLesson) => void;
  onEdit?: (lesson: SpeedReadingLesson) => void;
  onDelete?: (lesson: SpeedReadingLesson) => void;
  onReprocess?: (lesson: SpeedReadingLesson) => void;
  onReorder?: (newOrder: SpeedReadingLesson[]) => void;
}

const STATUS_CONFIG: Record<SpeedReadingLesson['status'], { label: string; badge: string }> = {
  READY:      { label: 'Hazır',        badge: 'bg-teal-50 text-teal-700 ring-1 ring-teal-200' },
  PROCESSING: { label: 'Üretiliyor…', badge: 'bg-blue-50 text-blue-600 ring-1 ring-blue-200' },
  DRAFT:      { label: 'Taslak',      badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  FAILED:     { label: 'Hata',        badge: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
};

const AudioIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13"/>
    <circle cx="6" cy="18" r="3"/>
    <circle cx="18" cy="16" r="3"/>
  </svg>
);

const GripIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" className="text-[#b0c8d4]">
    <circle cx="5" cy="4" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="5" cy="12" r="1.3"/>
    <circle cx="11" cy="4" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="11" cy="12" r="1.3"/>
  </svg>
);

const LessonGrid: React.FC<LessonGridProps> = ({
  lessons,
  canManage,
  onSelect,
  onEdit,
  onDelete,
  onReprocess,
  onReorder,
}) => {
  const [items, setItems] = useState<SpeedReadingLesson[]>(lessons);
  const dragIndexRef = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) setItems(lessons);
  }, [lessons, dragging]);

  if (lessons.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#dbe7ec] bg-white px-10 py-20 text-center">
        <p className="text-base font-bold text-[#174e67]">Henüz bir ders yok</p>
        <p className="mt-2 text-sm text-[#67808c]">
          {canManage
            ? 'Sağ üstteki "Yeni Ders" butonundan ilk dersini ekleyebilirsin.'
            : 'Yöneticiler ders yüklediğinde burada görünecek.'}
        </p>
      </div>
    );
  }

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
    setDragging(true);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    setOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      dragIndexRef.current = null;
      setOverIndex(null);
      setDragging(false);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    dragIndexRef.current = null;
    setOverIndex(null);
    setDragging(false);
    setItems(next);
    onReorder?.(next);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setOverIndex(null);
    setDragging(false);
  };

  return (
    <div className="flex flex-col divide-y divide-[#eef2f5] overflow-hidden rounded-2xl border border-[#e4edf2] bg-white shadow-[0_2px_16px_rgba(23,78,103,0.06)]">
      {items.map((lesson, index) => {
        const cfg = STATUS_CONFIG[lesson.status];
        const isReady = lesson.status === 'READY';
        const isFailed = lesson.status === 'FAILED';
        const isPending = lesson.status === 'DRAFT' || lesson.status === 'PROCESSING';
        const isDragged = dragging && dragIndexRef.current === index;
        const isOver = overIndex === index && dragIndexRef.current !== index;

        return (
          <div
            key={lesson.id}
            draggable={canManage}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`group relative flex items-center gap-3.5 px-4 py-3.5 transition-colors
              ${isDragged ? 'opacity-40 bg-[#f7fbfc]' : ''}
              ${isOver ? 'bg-[#edfbfc] ring-inset ring-2 ring-[#0ea5b7]/30' : ''}
              ${!isDragged && !isOver ? 'hover:bg-[#f7fbfc]' : ''}
            `}
          >
            {/* Drag handle */}
            {canManage ? (
              <div className="shrink-0 cursor-grab active:cursor-grabbing select-none opacity-0 group-hover:opacity-100 transition-opacity">
                <GripIcon />
              </div>
            ) : null}

            {/* Icon / Thumbnail */}
            <div className="shrink-0 flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#e8f5f7] text-[#0ea5b7]">
              {lesson.coverMedia?.url ? (
                <img src={lesson.coverMedia.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <AudioIcon />
              )}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-bold text-[#174e67] leading-snug">{lesson.title}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cfg.badge}`}>
                  {cfg.label}
                </span>
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-[#8aabb6]">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                </svg>
                <span>{formatLessonDuration(lesson.audioDurationMs)}</span>
                {isFailed && lesson.processingError ? (
                  <span className="ml-1 truncate text-red-500" title={lesson.processingError}>· {lesson.processingError}</span>
                ) : lesson.description && !isFailed ? (
                  <span className="ml-1 truncate text-[#8aabb6]">· {lesson.description}</span>
                ) : null}
              </div>
            </div>

            {/* Action area — always reserve space */}
            <div className="shrink-0 flex items-center gap-1">
              {/* Play / Spinner */}
              {isReady ? (
                <button
                  type="button"
                  onClick={() => onSelect(lesson)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0ea5b7] text-white shadow-[0_4px_12px_rgba(14,165,183,0.35)] transition hover:bg-[#0d95a5] active:scale-95"
                  title="Dersi Başlat"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </button>
              ) : isPending ? (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#eef6f8]">
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#0ea5b7] border-t-transparent" />
                </div>
              ) : (
                <div className="h-8 w-8" />
              )}

              {/* Manage actions — visible on hover */}
              {canManage ? (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => onEdit?.(lesson)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8aabb6] transition hover:bg-[#eef6f8] hover:text-[#174e67]"
                    title="Düzenle"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>

                  {(isFailed || lesson.status === 'DRAFT') && onReprocess ? (
                    <button
                      type="button"
                      onClick={() => onReprocess(lesson)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8aabb6] transition hover:bg-[#fff4e0] hover:text-[#a05d00]"
                      title="Yeniden İşle"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M21 21v-5h-5"/><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                    </button>
                  ) : null}

                  {onDelete ? (
                    <button
                      type="button"
                      onClick={() => onDelete(lesson)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-[#8aabb6] transition hover:bg-[#fef4f4] hover:text-[#a04c4c]"
                      title="Sil"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LessonGrid;
